import { Injectable } from '@angular/core';

import { ThresholdChartService } from './threshold.chart.service';
import { DateFormatterService } from '../../../providers/date.formatter/date.formatter.service';
import { TooltipService } from '../services/tooltip.service';
import { find, forEach, invoke, isEmpty } from 'lodash';
import * as Highcharts from 'highcharts';

@Injectable()
export class ThresholdChartTooltipsService {
  service: ThresholdChartService;

  constructor(
    thresholdChartService: ThresholdChartService,
    private readonly dateFormatterService: DateFormatterService,
    private tooltipService: TooltipService
  ) {
    this.service = thresholdChartService;
  }

  // eslint-disable-next-line
  formatBasicTooltip(tooltipContext: any, axisUnitLabel: (value: number) => string): string {
    let s = `<div class="tooltip-content">`;
    forEach(tooltipContext.points, function (point) {
      const color = point.series.options.lineColor || point.color;
      s += `<span style='color:${color}'>\u25CF</span> ${point.series.name}: <b>${axisUnitLabel(
        this.y
      )}}</b><br/>`;
    });
    return s + `<span>${this.dateFormatterService.getDateTime(tooltipContext.x)}</span>`;
  }

  // eslint-disable-next-line
  formatThresholdTooltip(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    widgetOptions: any,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    tooltip: any,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    formatterContext: any,
    axisUnitLabel: (value) => string,
    thresholds: any[] = [] //eslint-disable-line
  ): string {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    const _getSeriesTitle = this.service._getSeriesTitle;
    const _getFillColor = this.service._getFillColor.bind(this.service);
    const _getLineColor = this.service._getLineColor.bind(this.service);
    let lastValue = 0;
    let value = '';
    let failure = '';
    let fullTooltip = '';
    forEach(formatterContext.points, point => {
      if (point.series.options.id === 'threshold1') {
        thresholds[0] = point;
      } else if (point.series.options.id === 'threshold2') {
        thresholds[1] = point;
      } else if (lastValue <= point.y) {
        lastValue = point.y;

        if (point.series.options.id === 'separate') {
          value = `<span style="color: ${_getFillColor(point.key)}">${_getSeriesTitle(
            point.key
          )}:${axisUnitLabel(point.y)}</span><br/>`;
        } else {
          value = `<span style="color: ${_getLineColor(point.series.options.id)}">${
            point.series.name
          }:${axisUnitLabel(point.y)}</span><br/>`;

          if (point.series.options.id === 'failure') {
            // eslint-disable-next-line
            const failureInfo: any = find(tooltip.chart.series, {
              name: 'Failure'
            });
            if (failureInfo) {
              failure = `<span style="color: ${_getLineColor(point.series.options.id)}">● ${
                point.point.additionalTooltip[0].value
              } (${point.point.additionalTooltip[0].label})   </span><br/>`;
            }
          }

          if (point.series.options.id === 'good') {
            // eslint-disable-next-line
            const warning: any = find(tooltip.chart.series, {
              name: 'Fair'
            });
            if (warning && lastValue === warning.dataMax) {
              value = `<span style="color:${warning.color}">${warning.name}:${axisUnitLabel(
                point.y
              )}</span><br/>`;
            }
          }
        }
      }

      if (point.series.options.id !== 'threshold1' && point.series.options.id !== 'threshold2') {
        const findAdditionalTooltip = formatterContext.additionalTooltipInfo.find(
          formatter => formatter.id === point.series.options.id
        )?.data;
        if (findAdditionalTooltip?.length > 0) {
          const additionalTooltipInfo = findAdditionalTooltip.find(
            tooltip => tooltip.x === point.x
          );
          additionalTooltipInfo?.additionalTooltip?.forEach(element => {
            value = value + `<span>` + element.label + ' ' + element.value + `</span>` + '</br>';
          });
        }
      }
    });

    const goodColor = _getLineColor('good');
    const thresholdStringRepresentation = _this._getThresholdStringRepresentation(
      thresholds[0],
      thresholds[1],
      goodColor,
      widgetOptions,
      axisUnitLabel
    );
    if (value || thresholdStringRepresentation) {
      const pointTime = widgetOptions.showPinning
        ? '<div>' +
          this.tooltipService.getTooltipDate(
            formatterContext.x,
            formatterContext?.points?.length > 0
              ? formatterContext.points[0].series.options.aggregationRange
              : 0
          ) +
          '<br/>'
        : '';
      fullTooltip += `<div class="tooltip-content">${pointTime} ${value} ${failure} ${thresholdStringRepresentation}</div>`;
    }
    return fullTooltip;
  }

  _getThresholdStringRepresentation(
    fairPoint: any, // eslint-disable-line
    poorPoint: any, // eslint-disable-line
    goodColor: any, // eslint-disable-line
    widgetOptions: any, // eslint-disable-line
    axisUnitLabel: (value) => string
  ): string {
    if (!fairPoint || !poorPoint) {
      return '';
    }

    let stringPresentation = "<hr/><div class='thresholds'>Thresholds";
    const warningEnd = poorPoint.y;
    if (widgetOptions.reverseYAxis) {
      stringPresentation += `<div style="color: ${goodColor}"><span>Good: < ${axisUnitLabel(
        fairPoint.y
      )}</span>`;
      stringPresentation += `<div style="color: ${fairPoint.color}"><span>${
        fairPoint.series.name
      }: ${axisUnitLabel(fairPoint.y)} - ${axisUnitLabel(warningEnd)}</span>`;
      stringPresentation += `<div style="color: ${poorPoint.color}"><span>${
        poorPoint.series.name
      }: >= ${axisUnitLabel(poorPoint.y)}</span>`;
    } else {
      stringPresentation += `<div style="color: ${goodColor}"><span>Good: > ${axisUnitLabel(
        poorPoint.y
      )}
      </span>`;
      stringPresentation += `<div style="color: ${poorPoint.color}"><span>${
        fairPoint.series.name
      }: ${axisUnitLabel(fairPoint.y + 0.1)} - ${axisUnitLabel(warningEnd)}</span>`;
      stringPresentation += `<div style="color: ${fairPoint.color}"><span>${
        poorPoint.series.name
      }: <= ${axisUnitLabel(fairPoint.y)}</span>`;
    }

    return stringPresentation + '</div>';
  }
}

// will be executed on load
// eslint-disable-next-line
(<any>window).thresholdPins = {
  pinTooltip: function (chartId, params) {
    const chartObj = find(Highcharts.charts, function (chart) {
      if (!chart && isEmpty(chart)) {
        return false;
      }

      // eslint-disable-next-line
      return (chart as any).renderTo.attributes.id.value === chartId; // @TODO: this is a patch, not a fix
    });

    return invoke(chartObj, 'series[0].hcEvents.pinEvent[0].fn', params);
  },

  unpinTooltip: function (chartId, pinID) {
    const chart = find(Highcharts.charts, function (chart) {
      if (!chart && isEmpty(chart)) {
        return false;
      }
      // eslint-disable-next-line
      return (chart as any).renderTo.attributes.id.value === chartId; // @TODO: this is a patch, not a fix
    });

    return invoke(chart, 'series[0].hcEvents.unpinEvent[0].fn', {
      pinID: pinID
    });
  }
};
